import { useMutation } from 'react-query';

import { useToasts } from 'components/ToastProvider';
import { queryClient } from 'service/queryClient';
import {
  userRequirementsAttachmentsKey,
  useUserRequirementAttachmentsQuery,
} from 'Attachment/query/attachment.query';
import { attachmentApi } from 'Attachment/service/attachment.api';
import { AttachmentList } from 'Attachment/component/AttachmentList/AttachmentList';
import { FC } from 'react';
import { useMyProfileQuery } from 'MyProfile/query/myProfile.query';
import { getRoles } from 'MyProfile/model/MyProfile';
import { RoleType } from 'MyProfile/enum/RoleType';

export interface UserRequirementAttachmentListProps {
  userId: string;
  requirementId: string;
  onAttachmentSuccess:  {() : void} | null;
}

export const RequirementAttachmentList: FC<UserRequirementAttachmentListProps> = ({ userId, requirementId, onAttachmentSuccess }) => {
  const { showError, showSuccess } = useToasts();
  const { data, isLoading } = useUserRequirementAttachmentsQuery(userId, requirementId);
  const { mutate: uploadAttachment } = useMutation(
    (files: File[]) => attachmentApi.uploadFilesByUserAndRequirementId(files, userId, requirementId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(userRequirementsAttachmentsKey);
        onAttachmentSuccess && onAttachmentSuccess();
        showSuccess('Upload attachment success');
      },
      onError: () => showError('Upload attachment failed'),
    }
  );

  // Get current user roles
  const { data: myProfile } = useMyProfileQuery();
  const myRoles: RoleType[] = myProfile ? getRoles(myProfile) : [];
  const allowedUploadRoles: RoleType[] = [
    RoleType.Administrator,
    RoleType.LocalAdministrator,
    RoleType.HierarchicalManager,
    RoleType.Supervisor,
    RoleType.Approver,
    RoleType.ChannelPartnerManager
  ];
  const allowedRemoveRoles: RoleType[] = [
    RoleType.Administrator,
    RoleType.LocalAdministrator,
    RoleType.HierarchicalManager,
    RoleType.Supervisor,
    RoleType.Approver,
    RoleType.ChannelPartnerManager
  ];
  const canUpload = myRoles.length === 0 || myRoles.some(role => allowedUploadRoles.includes(role));
  const canRemove = myRoles.length === 0 || myRoles.some(role => allowedRemoveRoles.includes(role));
  return (
    <>
      <AttachmentList
        items={data?.result ?? []}
        loading={isLoading}
        allowRemoveAttachment={canRemove}
        canUpload={canUpload}
        // disableAddAttachment={data?.result?.length! >= 1 ?? false }
        onDropzoneSave={canUpload ? (files: File[]) => uploadAttachment(files) : undefined}
        userId={userId}
        requirementId={requirementId}
        filesLimit={10}
        noDataMessage="There are no attachments yet..."
        onRemoveRequirementAttachmentSuccess={onAttachmentSuccess}
      />
    </>
  );
};
 